function happysoySetValue(bundle, picks = []) {
  if (!Number.isSafeInteger(bundle?.price) || bundle.price < 0 || !Array.isArray(bundle.slots) || !bundle.slots.length) return null;
  let originalMin = 0, originalMax = 0;
  for (const [index, slot] of bundle.slots.entries()) {
    let options = (slot.options || []).filter(option => option.available === true);
    if (picks[index]) options = options.filter(option => String(option.variantId) === String(picks[index]));
    if (!options.length || options.some(option => !Number.isSafeInteger(option.price) || option.price < 0)) return null;
    originalMin += Math.min(...options.map(option => option.price));
    originalMax += Math.max(...options.map(option => option.price));
  }
  if (!Number.isSafeInteger(originalMin) || !Number.isSafeInteger(originalMax)) return null;
  const percent = original => original > bundle.price
    ? Number(BigInt(original - bundle.price) * 100n / BigInt(original)) : 0;
  return { originalMin, originalMax, savingMin: originalMin - bundle.price, savingMax: originalMax - bundle.price,
    percentMin: percent(originalMin), percentMax: percent(originalMax) };
}
function happysoySetImage(bundle, images = {}) {
  const type = {
    'd30efa65-d8b6-49a0-babe-27ce9a53f206': 'small',
    '76a905af-f67d-41cb-86b4-5b0c0fec5a48': 'medium',
    '8d904fc2-2ab3-4d41-be74-cc93eacc2d97': 'large',
    '4f42d236-cb5d-4527-9665-cb5dc3fc78e6': 'trio',
    '66ee9e16-ddd5-4518-b638-fa11b941a1f9': 'quartet',
  }[bundle?.typeId];
  const shapes = {
    small: 'brown,brown,matches', medium: 'brown,candles,matches,refill', large: 'brown,brown,candles,candles,matches,refill',
    trio: 'candles,candles,candles,refill', quartet: 'candles,candles,candles,candles,matches,refill',
    quartetDoubleRefill: 'candles,candles,candles,candles,matches,refill,refill',
  };
  if (!type || !Array.isArray(bundle.slots)) return '';
  const shape = bundle.slots.map(slot => {
    const options = (slot.options || []).filter(option => option.available === true);
    if (!options.length) return 'unknown';
    if (options.every(option => option.color === 'brown')) return 'brown';
    if (options.every(option => ['black', 'white'].includes(option.color))) return 'candles';
    if (options.every(option => /Streichhölzer/i.test(option.title))) return 'matches';
    if (options.every(option => String(option.variantId) === '44822101000457')) return 'refill';
    return 'unknown';
  }).sort().join(',');
  const imageKey = type === 'quartet' && shape === shapes.quartetDoubleRefill ? 'quartetDoubleRefill' : type;
  if (shape !== shapes[imageKey]) return '';
  try {
    const url = new URL(images[imageKey], window.location.origin);
    return images[imageKey] && url.protocol === 'https:' ? url.href : '';
  } catch { return ''; }
}
async function happysoySetJson(url, options = {}, timeout = 6000) {
  const controller = new AbortController();
  const externalSignal = options.signal;
  const cancel = () => controller.abort();
  if(externalSignal?.aborted) controller.abort();
  else externalSignal?.addEventListener('abort',cancel,{once:true});
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error('Die Verbindung antwortet nicht. Bitte versuche es später erneut.'));
      controller.abort();
    }, timeout);
  });
  const request = (async () => {
    const response = await fetch(url, {...options, signal:controller.signal});
    return {response, data:await response.json()};
  })();
  try { return await Promise.race([request, deadline]); }
  finally { clearTimeout(timer);externalSignal?.removeEventListener('abort',cancel); }
}
async function loadCatalogs(config) {
  if(config.currency !== 'EUR') throw new Error('Diese Sets sind nur in Euro verfügbar.');
  const {response,data}=await happysoySetJson('/apps/happysoy-bundles/api/bundles',{credentials:'same-origin',cache:'no-store'});
  if(!response.ok || !data.bundles?.length) throw new Error('Momentan sind keine Geschenksets verfügbar.');
  return {definitions:data.bundles,connected:false,checkoutEnabled:false};
}
class HappysoySetBuilder extends HTMLElement {
  connectedCallback() {
    if(this.shadowRoot) return;
    const config=JSON.parse(this.querySelector('[data-config]').textContent);
    const root=this.attachShadow({mode:'open'});
    root.append(this.querySelector('[data-ui]').content.cloneNode(true));
    this.start(root,config);
  }
  async start(root,config) {
const $ = selector => root.querySelector(selector);
const money = cents => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
const esc = text => String(text ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' })[c]);
const valueLabels = {original:'Einzeln',originalFrom:'Einzeln ab',saving:'Du sparst',savingFrom:'Du sparst mindestens',comparison:'Verglichen mit dem Einzelkauf.',imageNote:'Motive beispielhaft. Du wählst sie selbst.',...config.valueLabels};
const thumbnail = source => {
  try {
    const url=new URL(source);
    if(url.protocol === 'https:' && url.hostname === 'cdn.shopify.com') {
      url.searchParams.set('width','400');
      return url.href;
    }
  } catch {}
  return source;
};
let rules = {}, definitions = [], details = {}, mode, occasion = config.occasion, size, picks = [], colors = [], active = 0, expanded = false;
const colorPicker = document.createElement('div');
colorPicker.className = 'color-picker';
$('#options').before(colorPicker);
let busy = false, sessionToken, cartWriteLocked = false, pendingSetId = null;
let themeRequest = 0, themeController = null, themeLoading = false;
const pendingDefinitions = new Map();
const valueRequests = new Set();
$('#cart-link').textContent='Warenkorb';
$('#add').hidden=true;
const addRow = $('.add-row'), priceNote = addRow.nextElementSibling;
$('#cart').innerHTML='<div class="cart-heading"><p class="eyebrow">DEINE ZUSAMMENFASSUNG</p><h2>Dein Geschenkset</h2></div><div id="selection-summary"></div><p id="selection-note" class="fine" aria-live="polite"></p><p id="status" role="status" aria-live="polite"></p>';
$('#selection-summary').after(addRow);
if(priceNote?.classList.contains('fine')) addRow.after(priceNote);
const proxy = '/apps/happysoy-bundles';
const shopRoot = window.Shopify?.routes?.root || '/';
const cartUrl = shopRoot+'cart';
async function connect() {
  const [{response:status,data:state},{response,data:session}] = await Promise.all([
    happysoySetJson(proxy+'/api/status',{credentials:'same-origin',cache:'no-store'}),
    happysoySetJson(proxy+'/api/session',{credentials:'same-origin',cache:'no-store'})
  ]);
  if (!status.ok || !state.connected) throw new Error('Die Set-Verbindung ist momentan nicht erreichbar. Bitte versuche es später erneut.');
  // Read the current native-cart gate even when an older session was cached.
  if(!response.ok || typeof session.token !== 'string' || !session.token) throw new Error('Deine Set-Auswahl konnte nicht gestartet werden. Bitte lade die Seite neu.');
  sessionToken=session.token;
  try { sessionStorage.setItem('happysoy-set-session',sessionToken); } catch {}
  return {...state,nativeCartEnabled:session.nativeCartEnabled === true,customThemesEnabled:session.customThemesEnabled === true};
}
const optionsFor = index => (details[size]?.slots[index]?.options || []).filter(o => o.available);
const optionAt = index => optionsFor(index).find(o => String(o.variantId) === picks[index]);
const colorKey = option => ['black','white','brown'].includes(option.color) ? option.color : 'other';
const colorNames = {black:'Schwarz',white:'Weiß',brown:'Braun',other:'Weitere Artikel'};
const colorsFor = index => ['black','white','brown','other'].filter(color => optionsFor(index).some(o => colorKey(o) === color));
const neutralIcon = color => {
  if(color === 'other') return '<svg viewBox="0 0 40 52" aria-hidden="true"><path d="M9 9h22l3 36H6Z" fill="#eae3d8" stroke="#9b9588"/><path d="M10 14h20" stroke="#9b9588"/></svg>';
  const [glass,edge] = {black:['#30312e','#222320'],white:['#f8f7f2','#b9b7ae'],brown:['#87552f','#593b25']}[color];
  return `<svg viewBox="0 0 40 52" aria-hidden="true"><path d="M6 13h28v30c0 5-28 5-28 0Z" fill="${glass}" stroke="${edge}"/><ellipse cx="20" cy="13" rx="14" ry="4" fill="#eee6d7" stroke="${edge}"/><path d="M20 12v-4" stroke="#605746" stroke-width="1.5" stroke-linecap="round"/><path d="M10 20v20" stroke="#fff" opacity=".18" stroke-width="2" stroke-linecap="round"/></svg>`;
};
const complete = () => !!rules[size]?.slots.length && rules[size].slots.every((_, i) => optionAt(i));
async function loadDefinition(id) {
  if(details[id]) return;
  if(pendingDefinitions.has(id)) return pendingDefinitions.get(id);
  const request=(async () => {
    const {response,data}=await happysoySetJson(proxy+'/api/bundle?id='+encodeURIComponent(id),{credentials:'same-origin',cache:'no-store'});
    if(!response.ok || !data.bundle) throw new Error(data.error || 'Die Motive konnten nicht geladen werden.');
    if(data.bundle.revision !== rules[id].revision) throw new Error('Dieses Set wurde aktualisiert. Bitte die Seite neu laden.');
    details[id]=data.bundle;
    rules[id].slots=data.bundle.slots.map((_,i)=>'slot'+i);
    rules[id].labels=data.bundle.slots.map(s=>s.label);
  })();
  pendingDefinitions.set(id,request);
  try { await request; } finally { pendingDefinitions.delete(id); }
}
function reset() { picks = rules[size].slots.map((_,i) => optionsFor(i).length===1 ? String(optionsFor(i)[0].variantId) : null); colors = picks.map((_,i) => optionAt(i) ? colorKey(optionAt(i)) : colorsFor(i).length===1 ? colorsFor(i)[0] : null); active = Math.max(0,picks.findIndex(p=>!p)); expanded = false; }
function themeMessage(text, error = false) {
  $('#theme-message').textContent=text;
  $('#theme-message').dataset.error=String(error);
}
function renderThemeForm() {
  $('#theme-form').hidden=mode?.customThemesEnabled !== true;
  $('#theme-form').setAttribute('aria-busy',String(themeLoading));
  $('#theme-submit').disabled=themeLoading || busy || cartWriteLocked;
  $('#theme-submit').setAttribute('aria-busy',String(themeLoading));
  $('#theme-submit').textContent=themeLoading ? 'Motive werden gesucht …' : 'Passende Kerzen finden';
}
function cancelThemeSearch(clearMessage = false) {
  themeRequest++;themeController?.abort();themeController=null;themeLoading=false;
  if(clearMessage) themeMessage('');
  renderThemeForm();
}
function themeOffers(result) {
  const invalid=()=>new Error('Diese Motivauswahl konnte nicht sicher geladen werden. Bitte versuche es erneut oder wähle ein vorhandenes Thema.');
  if(!Array.isArray(result.themes) || result.themes.length < 1 || result.themes.length > 2 || !Array.isArray(result.themeIds)
    || result.themeIds.length !== result.themes.length || new Set(result.themeIds).size !== result.themeIds.length
    || result.themes.some(theme=>!theme || typeof theme.key !== 'string' || !result.themeIds.includes(theme.key) || typeof theme.label !== 'string' || !theme.label.trim())
    || new Set(result.themes.map(theme=>theme.key)).size !== result.themes.length
    || !Array.isArray(result.bundles) || result.bundles.length > 100) throw invalid();
  const offers=[],ids=new Set(),types=new Set();
  for(const bundle of result.bundles) {
    if(!bundle || typeof bundle.id !== 'string' || !/^ai-[a-f0-9]{64}$/.test(bundle.id) || ids.has(bundle.id)
      || !Number.isSafeInteger(bundle.revision) || bundle.revision < 1 || !Number.isSafeInteger(bundle.price) || bundle.price < 100
      || typeof bundle.typeId !== 'string' || !bundle.typeId || types.has(bundle.typeId)
      || typeof bundle.groupKey !== 'string' || !bundle.groupKey || typeof bundle.group !== 'string' || !bundle.group.trim()
      || typeof bundle.sizeName !== 'string' || !bundle.sizeName.trim()
      || !Array.isArray(bundle.slots) || !bundle.slots.length || bundle.slots.length > 8) throw invalid();
    ids.add(bundle.id);types.add(bundle.typeId);
    for(const slot of bundle.slots) {
      if(!slot || typeof slot.label !== 'string' || !slot.label.trim() || !Array.isArray(slot.options) || !slot.options.length) throw invalid();
      const variants=new Set();
      for(const option of slot.options) {
        if(!option || !/^\d{10,20}$/.test(String(option.variantId)) || variants.has(String(option.variantId)) || option.available !== true
          || typeof option.title !== 'string' || !option.title.trim() || typeof option.image !== 'string') throw invalid();
        if(option.image) try { if(new URL(option.image).protocol !== 'https:') throw invalid(); } catch { throw invalid(); }
        variants.add(String(option.variantId));
      }
    }
    offers.push(bundle);
  }
  if(new Set(offers.map(bundle=>bundle.groupKey)).size > 1) throw invalid();
  return offers;
}
async function searchTheme(event) {
  event.preventDefault();
  if(mode?.customThemesEnabled !== true || busy || cartWriteLocked || themeLoading) return;
  const text=$('#theme-input').value.trim();
  if(text.length < 3 || text.length > 240) { themeMessage('Beschreibe deinen Anlass bitte mit 3 bis 240 Zeichen.',true);$('#theme-input').focus();return; }
  cancelThemeSearch();
  const request=themeRequest,controller=new AbortController();
  themeController=controller;themeLoading=true;themeMessage('');renderThemeForm();
  try {
    const result=await api('/api/theme',{text},35000,controller.signal);
    if(request !== themeRequest) return;
    if(result.status !== 'matched') {
      const messages={
        unclear:'Beschreibe deinen Anlass etwas genauer oder wähle ein vorhandenes Thema. Deine aktuelle Auswahl bleibt erhalten.',
        none:'Für diese Beschreibung haben wir noch keine passende Kollektion gefunden. Wähle einen anderen Anlass oder ein vorhandenes Thema. Deine aktuelle Auswahl bleibt erhalten.',
        unavailable:'Für diesen Anlass gibt es derzeit kein vollständiges Set mit den vorgegebenen Kerzentypen. Deine aktuelle Auswahl bleibt erhalten.'
      };
      themeMessage(messages[result.status] || 'Die Motive konnten gerade nicht zugeordnet werden. Bitte versuche es erneut oder wähle ein vorhandenes Thema.',true);
      return;
    }
    if(Array.isArray(result.bundles) && !result.bundles.length) { themeMessage('Für diesen Anlass gibt es derzeit kein vollständiges Set mit den vorgegebenen Kerzentypen. Deine aktuelle Auswahl bleibt erhalten.',true);return; }
    const offers=themeOffers(result);
    if(!offers.length) { themeMessage('Für diesen Anlass gibt es derzeit kein vollständiges Set mit den vorgegebenen Kerzentypen. Deine aktuelle Auswahl bleibt erhalten.',true);return; }
    const previousType=rules[size]?.typeId,previousPicks=[...picks];
    const next=offers.find(bundle=>bundle.typeId===previousType) || offers.find(bundle=>bundle.legacySize==='medium') || offers[0];
    // Full server-issued offers use the same selection and cart path as saved sets.
    for(const id of Object.keys(rules)) if(id.startsWith('ai-')) {delete rules[id];delete details[id];}
    for(const bundle of offers) {
      rules[bundle.id]={...bundle,name:bundle.sizeName,hint:bundle.description || '',slots:bundle.slots.map((_,i)=>'slot'+i),labels:bundle.slots.map(slot=>slot.label)};
      details[bundle.id]=bundle;
    }
    size=next.id;occasion=next.groupKey;reset();
    if(previousType && previousType===next.typeId) {
      picks=picks.map((pick,index)=>optionsFor(index).some(option=>String(option.variantId)===previousPicks[index]) ? previousPicks[index] : pick);
      colors=picks.map((_,index)=>optionAt(index) ? colorKey(optionAt(index)) : colorsFor(index).length===1 ? colorsFor(index)[0] : null);
      active=Math.max(0,picks.findIndex(pick=>!pick));
    }
    renderBuilder();
    themeMessage('Passende Motive: '+result.themes.map(theme=>theme.label).join(' + '));
  } catch(error) {
    if(request===themeRequest) themeMessage(error.message || 'Die Themensuche ist gerade nicht erreichbar. Deine aktuelle Auswahl bleibt erhalten.',true);
  } finally {
    if(request===themeRequest) {themeController=null;themeLoading=false;renderThemeForm();}
  }
}
$('#theme-form').addEventListener('submit',searchTheme);
function renderOccasions() {
  const unavailable = busy || !mode?.connected || !size;
  $('#occasion').setAttribute('aria-busy',String(busy));
  $('#occasion').querySelectorAll('[data-occasion]').forEach(button => {
    const loading=!!pendingSetId && rules[pendingSetId]?.groupKey === button.dataset.occasion;
    button.setAttribute('aria-pressed',String(!!size && button.dataset.occasion === occasion));
    button.setAttribute('aria-disabled',String(unavailable));
    button.setAttribute('aria-busy',String(loading));
    button.dataset.loading=String(loading);
    button.querySelector('.tile-loading').hidden=!loading;
  });
}
async function switchSet(id) {
  cancelThemeSearch(themeLoading);
  pendingSetId=id;busy=true;renderBuilder();if(!cartWriteLocked)$('#status').textContent='Deine Motive werden geladen …';
  try { await loadDefinition(id);size=id;occasion=rules[id].groupKey;reset();if(!cartWriteLocked)$('#status').textContent=''; }
  catch(error) { $('#status').textContent=error.message; }
  finally { pendingSetId=null;busy=false;renderBuilder(); }
}
function valueMarkup(id, selected = []) {
  const bundle = details[id];
  const value = bundle && happysoySetValue({...bundle, price:rules[id].price}, selected);
  if (!value || value.savingMin <= 0) return '';
  const exact = value.originalMin === value.originalMax;
  const percent = value.percentMin > 0 ? ` (${value.percentMin} %)` : '';
  return `<span class="set-original">${esc(exact ? valueLabels.original : valueLabels.originalFrom)} ${money(value.originalMin)}</span><span class="set-saving">${esc(exact ? valueLabels.saving : valueLabels.savingFrom)} ${money(value.savingMin)}${percent}</span>`;
}
function renderSelectionValue() {
  const element = $('#selection-value');
  if (!element) return;
  element.innerHTML = valueMarkup(size, picks);
  element.hidden = !element.innerHTML;
}
function renderSizes() {
  const focusedSize = root.activeElement?.dataset.size;
  $('#sizes').innerHTML = Object.entries(rules).filter(([,rule])=>rule.groupKey===occasion).map(([key, rule]) => {
    const image = happysoySetImage(details[key], config.setImages);
    return `<button data-size="${key}" aria-pressed="${key === size}" aria-disabled="${busy}" aria-busy="${pendingSetId === key}" data-loading="${pendingSetId === key}">${image ? `<img class="set-image" src="${esc(image)}" alt="" width="1536" height="1024" loading="lazy" decoding="async">` : ''}<span class="set-card-body"><span class="set-card-name">${esc(rule.name)}</span><strong class="set-card-price">${money(rule.price)}</strong><span class="set-value">${valueMarkup(key)}</span>${pendingSetId === key ? '<small>Wird geladen…</small>' : ''}</span></button>`;
  }).join('');
  if (focusedSize) [...$('#sizes').querySelectorAll('[data-size]')].find(button => button.dataset.size === focusedSize)?.focus({preventScroll:true});
  const description = $('#set-description');
  if (description) { description.hidden = !rules[size]?.hint;description.querySelector('p').textContent = rules[size]?.hint || ''; }
  const note = $('#set-value-note');
  if (note) {
    const hasValues = !!$('#sizes .set-saving'), hasImages = !!$('#sizes .set-image');
    note.textContent = [hasValues ? valueLabels.comparison : '', hasImages ? valueLabels.imageNote : ''].filter(Boolean).join(' ');
    note.hidden = !note.textContent;
  }
}
function loadVisibleValues() {
  const ids = Object.keys(rules).filter(id => rules[id].groupKey === occasion && !details[id] && !valueRequests.has(id));
  for (const id of ids) valueRequests.add(id);
  let next = 0;
  void Promise.all(Array.from({length:Math.min(2,ids.length)}, async () => {
    while (next < ids.length) {
      const id = ids[next++];
      try {
        await loadDefinition(id);
        if (root.host.isConnected && rules[id]?.groupKey === occasion) { renderSizes();renderSelectionValue(); }
      } catch {}
    }
  }));
}
function renderBuilder() {
  renderThemeForm();
  renderOccasions();
  renderSizes();
  loadVisibleValues();
  $('#stage').innerHTML = rules[size].slots.map((source, i) => { const o = optionAt(i); return `<div class="stage-card"><button class="stage-item ${active === i ? 'active' : ''}" data-slot="${i}" aria-label="${esc(rules[size].labels[i])} ${o ? 'ändern' : 'auswählen'}">${o ? `${o.image ? `<img src="${esc(thumbnail(o.image))}" decoding="async" alt="${esc(o.title)}">` : `<span class="product-image-fallback" aria-hidden="true">${neutralIcon(colorKey(o))}</span>`}<span>${esc(rules[size].labels[i])}</span>` : `<span class="placeholder">+</span><span>${esc(rules[size].labels[i])}</span>`}</button>${o ? `<button class="remove-pick" data-clear="${i}" aria-label="${esc(rules[size].labels[i])} entfernen" title="Auswahl entfernen"><span aria-hidden="true">×</span></button>` : ''}</div>`; }).join('');
  $('#slots').innerHTML = rules[size].slots.map((source,i) => {
    const options = optionsFor(i);
    if(options.length === 1) return '';
    const selected = optionAt(i), types = selected ? [colorKey(selected)] : colors[i] ? [colors[i]] : colorsFor(i);
    return `<button class="slot-choice" data-slot="${i}" aria-pressed="${active === i}" aria-label="${esc(rules[size].labels[i])}: ${esc(types.map(c=>colorNames[c]).join(' / '))}${selected ? ', Motiv ausgewählt' : ', auswählen'}"><span class="slot-preview" aria-hidden="true">${types.map(neutralIcon).join('')}${selected ? '<span class="slot-check">✓</span>' : ''}</span><span>${esc(rules[size].labels[i])}<small>${esc(types.map(c=>colorNames[c]).join(' / '))}</small></span></button>`;
  }).join('');
  $('#pick-title').textContent = rules[size].labels[active];
  const availableColors = colorsFor(active), needsColor = availableColors.length > 1;
  colorPicker.hidden = !needsColor;
  colorPicker.innerHTML = needsColor ? `<p>Glasfarbe filtern</p><div class="color-choices" role="group" aria-label="Glasfarbe für ${esc(rules[size].labels[active])}"><button class="color-choice" data-color="all" aria-pressed="${!colors[active]}"><span>Alle</span></button>${availableColors.map(color => `<button class="color-choice" data-color="${color}" aria-pressed="${colors[active] === color}">${neutralIcon(color)}<span>${colorNames[color]}</span></button>`).join('')}</div>` : '';
  const opts = optionsFor(active).filter(o => !needsColor || !colors[active] || colorKey(o) === colors[active]), visible = needsColor || expanded ? opts : opts.slice(0,8);
  $('#options').innerHTML = visible.map(o => `<button class="option" data-variant="${esc(o.variantId)}" aria-pressed="${String(o.variantId) === picks[active]}"><span class="image">${o.image ? `<img src="${esc(thumbnail(o.image))}" decoding="async" loading="lazy" alt="">` : `<span class="product-image-fallback" aria-hidden="true">${neutralIcon(colorKey(o))}</span>`}${String(o.variantId) === picks[active] ? '<span class="check">✓</span>' : ''}</span><span>${esc(o.variantTitle !== 'Default Title' ? o.title+' · '+o.variantTitle : o.title)}</span>${o.color === 'black' || o.color === 'white' ? `<small>${o.color === 'black' ? 'Schwarzes' : 'Weißes'} Glas</small>` : ''}</button>`).join('');
  $('#more').hidden = needsColor || opts.length <= 8;
  $('#more').textContent = expanded ? 'Weniger Motive zeigen' : `Alle ${opts.length} Motive zeigen`;
  $('#price').textContent = money(rules[size].price);
  renderSelectionValue();
  $('#add').disabled = mode?.nativeCartEnabled !== true || busy || cartWriteLocked || !complete();
  $('#add').hidden = mode?.nativeCartEnabled !== true;
  $('#add').setAttribute('aria-busy',String(busy));
  $('#add').textContent = 'In den Warenkorb legen';
  $('#selection-summary').innerHTML = `<p class="summary-name">${esc(rules[size].group)} · ${esc(rules[size].name)}</p>` + rules[size].slots.map((_, i) => {
    const o = optionAt(i);
    return `<div class="summary-item">${o?.image ? `<img src="${esc(thumbnail(o.image))}" decoding="async" alt="">` : `<span class="summary-placeholder" aria-hidden="true">${neutralIcon(o ? colorKey(o) : colors[i] || colorsFor(i)[0] || 'other')}</span>`}<div><strong>${esc(rules[size].labels[i])}</strong><span>${o ? esc(o.title + (o.variantTitle && o.variantTitle !== 'Default Title' ? ' · '+o.variantTitle : '')) : 'Noch auswählen'}</span></div></div>`;
  }).join('');
  $('#selection-note').hidden = true;
  $('#selection-note').textContent = '';
}
async function api(route, data, timeout = 6000, signal) {
  if(!mode.connected) throw new Error('Die Kaufanbindung ist noch nicht aktiv.');
  const {response,data:result} = await happysoySetJson(proxy+route,{method:'POST',credentials:'same-origin',cache:'no-store',headers:{'Content-Type':'application/json','X-Happysoy-Session':sessionToken},body:JSON.stringify(data),signal},timeout);
  if(!response.ok) {
    if(result.code==='SESSION_EXPIRED') { try {sessionStorage.removeItem('happysoy-set-session');}catch{} }
    throw new Error(result.error || 'Die Auswahl konnte nicht übernommen werden. Bitte versuche es später erneut.');
  }
  return result;
}
function parentItems(result, quantity) {
  if(!Array.isArray(result?.items) || result.items.length !== 1) throw new Error('Das Geschenkset konnte nicht bestätigt werden. Bitte lade die Seite neu.');
  const item=result.items[0], id=item?.id;
  if((typeof id !== 'string' && !(typeof id === 'number' && Number.isSafeInteger(id))) || !/^\d{10,20}$/.test(String(id)) || item.quantity !== quantity
    || !item.properties || typeof item.properties !== 'object' || Array.isArray(item.properties)
    || Object.keys(item.properties).length > 40 || !Object.entries(item.properties).every(([key,value])=>key && key.length<=255 && typeof value==='string' && value.length<=4000)) {
    throw new Error('Das Geschenkset konnte nicht bestätigt werden. Bitte lade die Seite neu.');
  }
  // Only server-issued parent identity and properties go to Shopify. Never prices.
  return [{id:String(id),quantity,properties:{...item.properties}}];
}
async function addSet() {
  if(busy || cartWriteLocked || mode?.nativeCartEnabled !== true || !complete()) return;
  cancelThemeSearch(themeLoading);
  busy=true;$('#status').textContent='';renderBuilder();
  const set={bundleId:size,revision:rules[size].revision,quantity:1,variants:[...picks]};
  try {
    // A first native combination needs several Shopify operations. Bound that
    // preparation separately; it has not written anything to the cart yet.
    const items=parentItems(await api('/api/cart-item',{sets:[set]},20000),set.quantity);
    // Once sent, an error or timeout cannot prove Shopify rejected the write.
    // Do not replay, clear, or alter the cart; the existing cart link stays usable.
    cartWriteLocked=true;
    const {response,data}=await happysoySetJson(shopRoot+'cart/add.js',{method:'POST',credentials:'same-origin',cache:'no-store',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items})});
    const parent=items[0];
    // Automatic promotions can split a parent into several rows. Only count
    // positive quantities for the exact server-issued variant and properties.
    const confirmed=Array.isArray(data?.items) && data.items.reduce((quantity,item)=>quantity+
      (String(item.variant_id ?? item.id)===parent.id && Number.isSafeInteger(item.quantity) && item.quantity>0 && Object.entries(parent.properties).every(([key,value])=>item.properties?.[key]===value) ? item.quantity : 0),0)>=parent.quantity;
    if(!response.ok || !confirmed) throw new Error('Das Hinzufügen konnte nicht bestätigt werden.');
    location.assign(cartUrl);
  } catch(error) {
    $('#status').textContent=cartWriteLocked ? 'Das Hinzufügen konnte nicht bestätigt werden. Bitte prüfe deinen Warenkorb, bevor du das Set erneut hinzufügst.' : error.message;
  } finally { busy=false;renderBuilder(); }
}
root.addEventListener('click', async event => {
  const button = event.target.closest('button'); if (!button) return;
  if (button.id === 'cart-link') { location.assign(cartUrl);return; }
  if (busy) return;
  if (button.dataset.occasion) {
    cancelThemeSearch(true);$('#theme-input').value='';
    if (!mode?.connected || !size || button.dataset.occasion === occasion) return;
    const target=definitions.find(b=>b.groupKey===button.dataset.occasion && b.legacySize==='medium') || definitions.find(b=>b.groupKey===button.dataset.occasion);
    if(target) await switchSet(target.id);
    return;
  }
  if(themeLoading && (button.dataset.slot !== undefined || button.dataset.color || button.dataset.clear !== undefined || button.dataset.variant)) cancelThemeSearch(true);
  if (button.dataset.size) { await switchSet(button.dataset.size); }
  if (button.dataset.slot !== undefined) { active=Number(button.dataset.slot); expanded=false; renderBuilder(); }
  if (button.dataset.color) {
    const color=button.dataset.color;
    if(color !== 'all' && !colorsFor(active).includes(color)) return;
    // Filtering changes only the visible motifs, never the selected candle.
    colors[active]=color === 'all' ? null : color;expanded=false;renderBuilder();
  }
  if (button.dataset.clear !== undefined) {
    active=Number(button.dataset.clear);picks[active]=null;expanded=false;renderBuilder();
    $(`#stage [data-slot="${active}"]`).focus({preventScroll:true});
  }
  if (button.dataset.variant) { picks[active]=button.dataset.variant;renderBuilder(); }
  if (button.id === 'more') { expanded=!expanded; renderBuilder(); }
  if (button.id === 'add') await addSet();

});
try {
  const [catalog,connection]=await Promise.all([
    loadCatalogs(config).then(catalog => {
      definitions=catalog.definitions;
      rules=Object.fromEntries(definitions.map(b=>[b.id,{...b,name:b.sizeName,hint:b.description || ({small:'Kerze + Streichhölzer',medium:'Kerze + Refill',large:'Drei Lieblingskerzen'}[b.legacySize] || ''),slots:[],labels:[]}]));
      const groups=[...new Map(definitions.map(b=>[b.groupKey,b.group])).entries()];
      $('#occasion').innerHTML=groups.map(([key,title])=>`<button type="button" data-occasion="${esc(key)}" aria-pressed="false" aria-disabled="true">${esc(title)}<span class="tile-loading" hidden>Wird geladen…</span></button>`).join('');
      return catalog;
    }),
    connect()
  ]);
  mode={...catalog,...connection};
  const handle=new URL(location.href).searchParams.get('bundle');
  if(handle && !definitions.some(b=>b.handle===handle)) throw new Error('Dieses Geschenkset ist derzeit nicht verfügbar. Bitte öffne die Geschenkset-Übersicht.');
  const initial=definitions.find(b=>b.handle===handle) || definitions.find(b=>b.groupKey===occasion&&b.legacySize==='medium') || definitions.find(b=>b.groupKey===occasion) || definitions[0];
  size=initial.id;occasion=initial.groupKey;await loadDefinition(size);reset();renderBuilder();
  $('#mode').textContent = 'Dein Geschenkset · zum festen Setpreis';

} catch(error) { $('#status').textContent=error.message; }

  }
}
if(!customElements.get('happysoy-set-builder')) customElements.define('happysoy-set-builder',HappysoySetBuilder);
